import React, { useState, useEffect, useMemo } from 'react';
import PerformanceCalendar from './components/Gamification/PerformanceCalendar';
import CardStack from './components/Deck/CardStack';
import WelcomeOverlay from './components/WelcomeOverlay';
import ResultsView from './components/ResultsView';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import { swipeTickerService } from './services/swipetickerService';
import { generateAnonymousName, SYMBOLS } from './config/constants';

// v1.2.0 - Huge Header, Fixed Logo, Fixed Overlaps

const generateMockChartData = (symbol) => {
    // MOCK DATA GENERATOR - Only used if backend fails completely
    const points = 25;
    let lastClose = 100 + Math.random() * 200;
    const history = [];
    for (let i = 0; i < points; i++) {
        const open = lastClose;
        const change = (Math.random() - 0.47) * 4;
        const close = open + change;
        history.push({
            open, close,
            high: Math.max(open, close) + Math.random(),
            low: Math.min(open, close) - Math.random(),
            time: new Date(Date.now() - (points - i) * 86400000).toISOString().split('T')[0]
        });
        lastClose = close;
    }
    return {
        id: Math.random().toString(36).substr(2, 9),
        ticker: symbol, symbol, history,
        pattern: '2-2', status: 'neutral'
    };
};

import { AuthProvider } from '../components/auth/AuthProvider';

const SwipeTickerContent = () => {
    const [showWelcome, setShowWelcome] = useState(true);
    const [queue, setQueue] = useState([]);
    const [userId, setUserId] = useState(null);
    const [history, setHistory] = useState([]);
    const [timeframe, setTimeframe] = useState('1D');
    const [showResults, setShowResults] = useState(false);
    const [activeTab, setActiveTab] = useState('swipe');
    const [showShortcuts, setShowShortcuts] = useState(false);

    // Limits
    const BUY_LIST_LIMIT = 5;
    const WATCHLIST_LIMIT = 10;

    // Counts
    const buyListCount = history.filter(h => h.action === 'BUY_LIST').length;
    const watchlistCount = history.filter(h => h.action === 'SHORTLIST').length;
    const totalDeckSize = useMemo(() => queue.length + history.length, [queue, history]);

    useEffect(() => {
        const hasSeenWelcome = localStorage.getItem('swipeticker_welcomed');
        if (hasSeenWelcome) setShowWelcome(false);

        const initIdentity = async () => {
            try {
                const identity = await swipeTickerService.getOrCreateIdentity();
                setUserId(identity.user_id);
                localStorage.setItem('swipeticker_uid', identity.user_id);
            } catch (e) { console.error("Identity Init Failed", e); }
        };
        initIdentity();

        const loadDeck = async () => {
            try {
                const deck = await swipeTickerService.getDailyDeck();
                if (deck && deck.length > 0) {
                    setQueue(deck.map(d => ({
                        id: d.ticker, ticker: d.ticker, symbol: d.ticker,
                        history: d.history, pattern: d.pattern || '2-2', status: d.status || 'neutral'
                    })));
                } else {
                    // Force mock only if deck is truly empty
                    const mockQueue = Array.from({ length: 20 }).map(() =>
                        generateMockChartData(SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)])
                    );
                    setQueue(mockQueue);
                }
            } catch (e) {
                console.error("Deck load failed", e);
                const mockQueue = Array.from({ length: 20 }).map(() =>
                    generateMockChartData(SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)])
                );
                setQueue(mockQueue);
            }
        };
        loadDeck();
    }, []);

    const handleSwipe = async (direction) => {
        if (!queue[0]) return;
        const card = queue[0];
        const actionMap = { 'left': 'PASS', 'up': 'SHORTLIST', 'right': 'BUY_LIST' };

        if (userId) {
            try {
                await swipeTickerService.recordSwipe(
                    userId, card.ticker, actionMap[direction],
                    parseFloat(card.history[card.history.length - 1]?.close || 0)
                );
            } catch (e) { console.error("Swipe record failed", e); }
        }

        setHistory(prev => [...prev, { ticker: card.ticker, action: actionMap[direction] }]);
        setQueue(prev => prev.slice(1));
    };

    // Keyboard Shortcuts Implementation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (activeTab !== 'swipe' || showResults || queue.length === 0) return;

            switch (e.key) {
                case 'ArrowLeft':
                    handleSwipe('left');
                    break;
                case 'ArrowRight':
                    handleSwipe('right');
                    break;
                case 'ArrowUp':
                    handleSwipe('up');
                    break;
                case 'x':
                case 'X':
                    handleSwipe('up');
                    break;
                case 'd':
                case 'D':
                    setTimeframe('1D');
                    break;
                case 'w':
                case 'W':
                    setTimeframe('1W');
                    break;
                case 'm':
                case 'M':
                    setTimeframe('1M');
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [activeTab, showResults, queue, userId, timeframe]); // Dependencies critical for closure capture

    const handleFinish = () => setShowResults(true);
    const handleRestart = () => window.location.reload();

    // New Flow: Save -> Performance Tab
    const handleSaveAndContinue = () => {
        setShowResults(false);
        setActiveTab('performance');
    };

    if (showResults) return (
        <ResultsView
            history={history}
            onRestart={handleRestart}
            onSaveAndContinue={handleSaveAndContinue}
        />
    );

    return (
        <div className="min-h-screen bg-gray-50 overflow-hidden relative selection:bg-cyan-100">
            {/* GIANT Branding Header */}
            <div className="fixed top-0 left-0 right-0 h-32 md:h-48 z-10 flex flex-col items-center justify-end pb-4 bg-gradient-to-b from-gray-50 via-gray-50 to-transparent pointer-events-none">
                <div className="flex items-center gap-4 animate-fade-in-down">
                    {/* User Requested SVG Logo */}
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" fill="#0891b2" className="drop-shadow-lg h-16 w-16 md:h-20 md:w-20">
                        <path d="M245-400q-51-64-78-141t-27-159q0-27 3-54t9-54l-70 70-42-42 140-140 140 140-42 42-65-64q-7 25-10 50.5t-3 51.5q0 70 22.5 135.5T288-443l-43 43Zm413 273q-23 8-46.5 7.5T566-131L304-253l18-40q10-20 28-32.5t40-14.5l68-5-112-307q-6-16 1-30.5t23-20.5q16-6 30.5 1t20.5 23l148 407-100 7 131 61q7 3 15 3.5t15-1.5l157-57q31-11 45-41.5t3-61.5l-55-150q-6-16 1-30.5t23-20.5q16-6 30.5 1t20.5 23l55 150q23 63-4.5 122.5T815-184l-157 57Zm-90-265-54-151q-6-16 1-30.5t23-20.5q16-6 30.5 1t20.5 23l55 150-76 28Zm113-41-41-113q-6-16 1-30.5t23-20.5q16-6 30.5 1t20.5 23l41 112-75 28Zm8 78Z" />
                    </svg>
                    <h1 className="text-5xl md:text-7xl font-black tracking-tighter text-gray-900 drop-shadow-sm">
                        Swipe<span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">Ticker</span>
                    </h1>
                </div>
                <div className="text-xs md:text-sm font-bold tracking-[0.4em] text-gray-300 uppercase mt-2">
                    Tinder for Stocks
                </div>
            </div>

            {activeTab === 'swipe' ? (
                <CardStack
                    queue={queue}
                    onSwipe={handleSwipe}
                    buyListCount={buyListCount}
                    watchlistCount={watchlistCount}
                    buyListLimit={BUY_LIST_LIMIT}
                    watchlistLimit={WATCHLIST_LIMIT}
                    timeframe={timeframe}
                    onFinish={handleFinish}
                    totalDeckSize={totalDeckSize}
                    onToggleShortcuts={() => setShowShortcuts(true)}
                />
            ) : (
                <div className="pt-48 pb-24">
                    <PerformanceCalendar history={history} />
                </div>
            )}

            {/* Tab Navigation - Fixed Bottom Center */}
            <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-40 bg-white/90 backdrop-blur-xl rounded-full p-1.5 shadow-xl border border-gray-200/50 flex gap-1">
                <button
                    onClick={() => setActiveTab('swipe')}
                    className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${activeTab === 'swipe' ? 'bg-gray-900 text-white shadow-md' : 'text-gray-400 hover:text-gray-600'}`}
                >
                    Swipe
                </button>
                <button
                    onClick={() => setActiveTab('performance')}
                    className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${activeTab === 'performance' ? 'bg-gray-900 text-white shadow-md' : 'text-gray-400 hover:text-gray-600'}`}
                >
                    Performance
                </button>
            </div>

            {showWelcome && <WelcomeOverlay onDismiss={() => { localStorage.setItem('swipeticker_welcomed', 'true'); setShowWelcome(false); }} />}

            <KeyboardShortcuts isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} />

        </div>
    );
};

const SwipeTickerApp = () => (
    <AuthProvider>
        <SwipeTickerContent />
    </AuthProvider>
);

export default SwipeTickerApp;
